/* Attaches a live stone to the contact form, so Rich knows exactly which
   stone the visitor meant. */
import { FeedError, feedBase, getStone, stoneSummary, stoneTitle } from "./diamond-feed.js";

const SOLD = "This stone has just been reserved or sold.";

// Each form's revision: bumped by anything the visitor or the page does to
// its stone (attach, detach, a change of interest), so a lookup that answers
// late can tell it has been overtaken.
const revisions = new WeakMap();
const revision = (form) => revisions.get(form) ?? 0;
const bump = (form) => revisions.set(form, revision(form) + 1);

function show(form, value, summary) {
  const interest = form.querySelector("[data-enquiry-interest-select]");
  if (!interest) return false; // Shopify is showing the contact success state.
  bump(form);
  interest.value = "Diamond sourcing";
  const field = form.querySelector("[data-stone-field]");
  field.value = value;
  field.disabled = false;
  form.querySelector("[data-stone-summary]").textContent = summary;
  form.querySelector("[data-stone-context]").hidden = false;
  return true;
}

export function attachStone(form, stone, now = new Date()) {
  const detail = `${stone.carat.toFixed(2)} ct ${stone.shape.toLowerCase()}, ${stone.colour}, ${stone.clarity}`;
  return show(form, stoneSummary(stone, now), `This stone is attached: ${detail}.`);
}

// Why only the certificate is attached, as Rich reads it in the enquiry.
const REASONS = {
  sold: "reserved or sold",
  "not-found": "no longer listed",
};

export function attachCertificate(form, id, reason) {
  return show(
    form,
    `Certificate ${id} (${REASONS[reason] ?? "couldn't be checked"} when this enquiry was started)`,
    "A stone you looked at is attached by its certificate number.",
  );
}

export function detachStone(form) {
  bump(form);
  const field = form.querySelector("[data-stone-field]");
  field.value = "";
  field.disabled = true;
  form.querySelector("[data-stone-summary]").textContent = "";
  form.querySelector("[data-stone-context]").hidden = true;
}

export function detachUnlessDiamond(form) {
  const interest = form.querySelector("[data-enquiry-interest-select]");
  if (interest && interest.value !== "Diamond sourcing") detachStone(form);
}

// The visitor picked an interest: their choice now, whatever a lookup still
// on its way says.
export function interestChanged(form) {
  bump(form);
  detachUnlessDiamond(form);
}

// A linked stone, looked up and attached, unless the visitor has done
// anything to the form's stone or interest before the answer arrives.
export function attachFromURL(form, id, { base = feedBase(), fetch } = {}) {
  return attachLookup(form, id, getStone(base, id, { fetch }));
}

// The answer to a lookup already on its way (getStone's promise), attached
// the same way, so a page that shows the stone asks for it only once.
export async function attachLookup(form, id, lookup) {
  const started = revision(form);
  try {
    const { stone, available } = await lookup;
    if (revision(form) !== started) return;
    if (available) {
      attachStone(form, stone);
      return;
    }
    if (attachCertificate(form, id, "sold")) {
      form.querySelector("[data-stone-summary]").textContent =
        `${SOLD} ${stoneTitle(stone)} is attached by its certificate number.`;
    }
  } catch (error) {
    if (revision(form) !== started) return;
    // Whatever went wrong, Rich still learns which certificate was meant.
    const delisted = error instanceof FeedError && error.kind === "not-found";
    attachCertificate(form, id, delisted ? "not-found" : "unchecked");
  }
}

// On Shopify the script tag loads this module as stone-enquiry.js?v=… while the
// diamond selector imports ./stone-enquiry.js, so it can be evaluated twice;
// the page code below runs once.
if (typeof document !== "undefined" && !document.documentElement.dataset.stoneEnquiry) {
  document.documentElement.dataset.stoneEnquiry = "on";
  document.addEventListener("click", (event) => {
    if (!event.target.closest?.("[data-clear-stone]")) return;
    const form = event.target.closest("form");
    if (form) detachStone(form);
  });
  document.addEventListener("change", (event) => {
    if (!event.target.matches?.("[data-enquiry-interest-select]")) return;
    const form = event.target.closest("form");
    if (form) interestChanged(form);
  });
  // The diamond selector and the stone page attach their stone themselves.
  const id = new URLSearchParams(location.search).get("stone");
  const form = document.querySelector(".enquiry-form");
  const own = document.querySelector("diamond-selector") || document.querySelector("[data-stone-page]");
  if (id && /^\d+$/.test(id) && form && feedBase() && !own) attachFromURL(form, id);
}
